import { useGetUserNameWithFallback } from '@pba/react-utils';
import type { CommentableContentObject, Reply } from 'models';
import { type RefObject, useState } from 'react';
import { Avatar, Col, type RichTextMentions, RichTextRenderer, Row, Title } from 'ui';
import { Attachments } from 'features/attachments';
import { Translation } from 'features/translations';
import { ContentObjectAuthor } from '../../content-object-header';
import { CreateCommentForm } from '../../create-comment-form';
import { EditCommentForm } from '../../edit-comment-form';
import { CommentActions } from './comment-actions';
import { CommentFooter } from './comment-footer';

type P = {
  canComment: boolean;
  canDeleteComment: (comment: Reply) => boolean;
  comment: Reply;
  contentObjectId: string;
  contentObjectType: CommentableContentObject['type'];
  onRequestMentions: RichTextMentions;
  scrollRef?: RefObject<HTMLDivElement | null>;
  topParentShareableId?: string;
};

const Comment = ({
  canComment,
  canDeleteComment,
  comment,
  contentObjectId,
  contentObjectType,
  onRequestMentions,
  scrollRef,
  topParentShareableId,
}: P) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const getUserNameWithFallback = useGetUserNameWithFallback();

  return (
    <Row className="comment" gutter={['xs', 0]} ref={scrollRef}>
      <Col className="comment__author-photo">
        <ContentObjectAuthor author={comment.author}>
          <Avatar size="lg" src={comment.author.profilePhoto} />
        </ContentObjectAuthor>
      </Col>
      <Col className="comment__body">
        <div className="comment__message">
          <Row className="comment__header" justify="space-between">
            <Col>
              <ContentObjectAuthor author={comment.author}>
                <Title level={4}>
                  {getUserNameWithFallback({
                    firstName: comment.author.firstName,
                    lastName: comment.author.lastName,
                  })}
                </Title>
              </ContentObjectAuthor>
            </Col>
            <Col>
              <CommentActions
                canDeleteComment={canDeleteComment}
                comment={comment}
                contentObjectId={contentObjectId}
                contentObjectType={contentObjectType}
                onEditObject={() => {
                  setIsEditing(true);
                }}
                topParentShareableId={topParentShareableId}
              />
            </Col>
          </Row>
          <div>
            {isEditing ? (
              <EditCommentForm
                comment={comment}
                contentObjectId={contentObjectId}
                onRequestMentions={onRequestMentions}
                onSuccess={() => {
                  setIsEditing(false);
                }}
              />
            ) : (
              <>
                {comment.htmlContent ? <RichTextRenderer data={comment.htmlContent} /> : null}
                {comment.textContent ? <Translation id={comment.id} type={comment.type} /> : null}
                {comment.attachments.length > 0 ? <Attachments attachments={comment.attachments} /> : null}
              </>
            )}
          </div>
        </div>

        <CommentFooter
          comment={comment}
          contentObjectId={contentObjectId}
          onReplyClick={
            canComment
              ? () => {
                  setIsReplying((previousState) => !previousState);
                }
              : undefined
          }
        />

        {isReplying ? (
          <CreateCommentForm
            comment={comment}
            contentObjectId={contentObjectId}
            initialMode="input"
            onRequestMentions={onRequestMentions}
            onSuccess={() => {
              setIsReplying(false);
            }}
          />
        ) : null}
      </Col>
    </Row>
  );
};

export default Comment;
